import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, post, patch } from "../../lib/request"

function normalizeClientIds(arg) {
  const raw = Array.isArray(arg)
    ? arg
    : arg != null && String(arg).trim() !== ""
      ? [arg]
      : []
  const seen = new Set()
  const ids = []
  for (const id of raw) {
    if (id == null || String(id).trim() === "") continue
    const k = String(id)
    if (seen.has(k)) continue
    seen.add(k)
    ids.push(id)
  }
  return ids
}

function noteAuthorKey(n) {
  const author = n?.author ?? n?.authorId ?? n?.author_id
  if (author == null || String(author).trim() === "") return ""
  return String(author).trim()
}

function normalizeGuestNoteRow(n, fallbackClientId) {
  const author = noteAuthorKey(n)
  return {
    id: n.id,
    author,
    clientId: n.clientId ?? fallbackClientId,
    note: n.note ?? "",
    rating: n.rating,
  }
}

function extractNotesList(res) {
  if (Array.isArray(res?.data)) return res.data
  if (Array.isArray(res)) return res
  return []
}

/**
 * GET /api/v1/notes/clients/{clientId}
 * @param {string|number|(string|number)[]} clientIdsArg
 */
export const fetchGuestNotes = createAsyncThunk(
  "guestNotes/fetch",
  async (clientIdsArg, { rejectWithValue, getState }) => {
    try {
      const clientIds = normalizeClientIds(clientIdsArg)
      if (clientIds.length === 0) throw new Error("clientId(s) required")

      const loaded = getState().guestNotes?.loadedClientIds || {}
      const pending = clientIds.filter((id) => !loaded[String(id)])
      if (pending.length === 0) {
        return { byAuthor: {}, clientIds: [], skipped: true }
      }

      const byAuthor = {}
      for (const clientId of pending) {
        const res = await get(`notes/clients/${clientId}`)
        const list = extractNotesList(res)
        for (const n of list) {
          const author = noteAuthorKey(n)
          if (!author) continue
          if (!byAuthor[author]) byAuthor[author] = []
          byAuthor[author].push(normalizeGuestNoteRow(n, clientId))
        }
      }

      return { byAuthor, clientIds: pending }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch guest notes")
    }
  },
  {
    condition: (clientIdsArg, { getState }) => {
      const clientIds = normalizeClientIds(clientIdsArg)
      if (clientIds.length === 0) return false
      const loaded = getState().guestNotes?.loadedClientIds || {}
      return clientIds.some((id) => !loaded[String(id)])
    },
  },
)

/**
 * POST /api/v1/notes
 * Body: { author, clientId, note, rating }
 */
export const createGuestNote = createAsyncThunk(
  "guestNotes/create",
  async ({ author, clientId, note, rating }, { rejectWithValue }) => {
    try {
      if (!author) throw new Error("author is required")
      if (clientId == null) throw new Error("clientId is required")

      const res = await post("notes", {
        author: String(author),
        clientId: Number(clientId),
        note: note || "",
        rating: rating != null ? Number(rating) : undefined,
      })
      const created = res?.data ?? res
      return normalizeGuestNoteRow(
        { ...created, author: created?.author ?? author, clientId: created?.clientId ?? clientId },
        clientId,
      )
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create guest note")
    }
  },
)

/**
 * PATCH /api/v1/notes/{id}
 */
export const updateGuestNote = createAsyncThunk(
  "guestNotes/update",
  async ({ id, author, clientId, note, rating }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("note id is required")
      if (!author) throw new Error("author is required")
      if (clientId == null) throw new Error("clientId is required")

      const res = await patch(`notes/${id}`, {
        author: String(author),
        clientId: Number(clientId),
        note: note || "",
        rating: rating != null ? Number(rating) : undefined,
      })
      const updated = res?.data ?? res
      return normalizeGuestNoteRow(
        {
          ...updated,
          id: updated?.id ?? id,
          author: updated?.author ?? author,
          clientId: updated?.clientId ?? clientId,
        },
        clientId,
      )
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update guest note")
    }
  },
)

/** Clear guest note via PATCH /api/v1/notes/{id} with blank note and rating 0. */
export const removeGuestNote = createAsyncThunk(
  "guestNotes/remove",
  async ({ id, author, clientId }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("note id is required")
      if (!author) throw new Error("author is required")
      if (clientId == null || clientId === "") throw new Error("clientId is required")

      await patch(`notes/${id}`, {
        author: String(author),
        clientId: Number(clientId),
        note: "",
        rating: 0,
      })
      return { id, author: String(author) }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to remove guest note")
    }
  },
)

const initialState = {
  notesByAuthor: {},
  loadedClientIds: {},
  status: "idle",
  error: null,
}

const guestNotesSlice = createSlice({
  name: "guestNotes",
  initialState,
  reducers: {
    clearGuestNote: (state, action) => {
      const author = action.payload
      if (author == null) return
      delete state.notesByAuthor[author]
      delete state.notesByAuthor[String(author)]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGuestNotes.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchGuestNotes.fulfilled, (state, action) => {
        state.status = "succeeded"
        const { byAuthor, clientIds, skipped } = action.payload
        if (!skipped) {
          for (const id of clientIds || []) {
            state.loadedClientIds[String(id)] = true
          }
        }
        for (const [author, data] of Object.entries(byAuthor || {})) {
          state.notesByAuthor[author] = Array.isArray(data) ? data : []
        }
      })
      .addCase(fetchGuestNotes.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
      .addCase(createGuestNote.fulfilled, (state, action) => {
        const row = action.payload
        const author = row?.author
        if (!author) return
        const existing = [...(state.notesByAuthor[author] || [])]
        existing.push(row)
        state.notesByAuthor[author] = existing
      })
      .addCase(updateGuestNote.fulfilled, (state, action) => {
        const row = action.payload
        const author = row?.author
        if (!author) return
        const list = [...(state.notesByAuthor[author] || [])]
        const idx = row.id != null ? list.findIndex((n) => n.id === row.id) : -1
        if (idx >= 0) list[idx] = row
        else if (list.length > 0) list[0] = { ...list[0], ...row }
        else list.push(row)
        state.notesByAuthor[author] = list
      })
      .addCase(removeGuestNote.fulfilled, (state, action) => {
        const author = action.payload?.author
        if (author == null) return
        delete state.notesByAuthor[author]
        delete state.notesByAuthor[String(author)]
      })
  },
})

export const { clearGuestNote } = guestNotesSlice.actions
export default guestNotesSlice.reducer
